import React, { useEffect, useState } from "react";
import Sidebar from "../../components/SideBar";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";

const ReviewManagement = () => {
  const [activePage, setActivePage] = useState("Reviews");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);

  const buildCandidateUrls = (path) => {
    const base = (API_BASE_URL || "").replace(/\/$/, "");
    const cleanPath = (path || "").replace(/^\//, "");
    return [`${base}/${cleanPath}`, `${base}/api/${cleanPath}`];
  };

  const readResponseBody = async (response) => {
    const contentType = response.headers?.get?.("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }
    const text = await response.text();
    return { _nonJson: true, text };
  };

  const fetchFirstOk = async (path, options) => {
    let lastError;
    for (const url of buildCandidateUrls(path)) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const body = await readResponseBody(response);
          lastError = new Error(
            body && body._nonJson
              ? `Request failed (${response.status})`
              : body?.error || body?.message || `Request failed (${response.status})`
          );
          continue;
        }
        const body = await readResponseBody(response);
        if (body && body._nonJson) {
          lastError = new Error("Unexpected non-JSON response");
          continue;
        }
        return body;
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error("Request failed");
  };

  const buildSortValue = (review) => {
    const raw = review?.createdAt || review?.date || review?.updatedAt;
    const time = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  };

  const applyClientFilters = (items, { status, search }) => {
    const searchText = (search || "").trim().toLowerCase();
    return items
      .filter((review) => {
        if (status === "published" && !review.isPublished) return false;
        if (status === "pending" && review.isPublished) return false;
        if (!searchText) return true;

        const haystack = [
          review.title,
          review.content,
          review.userName,
          review.userEmail,
          review.productId,
          review.productName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(searchText);
      })
      .sort((a, b) => buildSortValue(b) - buildSortValue(a));
  };

  const fetchReviewsViaProducts = async () => {
    const products = await fetchFirstOk("products/getallproducts");
    const productList = Array.isArray(products)
      ? products.filter((p) => !p?.isDeleted)
      : [];

    const reviewBatches = await Promise.all(
      productList.map(async (product) => {
        try {
          const payload = await fetchFirstOk(
            `reviews/product/${product._id}?includeUnpublished=true&sortBy=latest`
          );
          const rows = Array.isArray(payload?.reviews) ? payload.reviews : [];
          return rows.map((review) => ({
            ...review,
            productName: product.productName || "",
          }));
        } catch (err) {
          return [];
        }
      })
    );

    return reviewBatches.flat();
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sortBy: "latest",
        status: statusFilter,
        search: searchQuery,
      });
      let list = [];
      try {
        const data = await fetchFirstOk(`reviews/admin?${params}`);
        list = Array.isArray(data?.reviews) ? data.reviews : [];
      } catch (err) {
        const allReviews = await fetchReviewsViaProducts();
        list = applyClientFilters(allReviews, {
          status: statusFilter,
          search: searchQuery,
        });
      }

      setReviews(list);
      setError("");
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Unable to load reviews");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [statusFilter, searchQuery]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handlePublishToggle = async (review) => {
    const nextStatus = !review.isPublished;
    const confirmationText = nextStatus
      ? "Publish this review on user site?"
      : "Unpublish this review from user site?";
    if (!window.confirm(confirmationText)) {
      return;
    }

    try {
      const publishedBy = localStorage.getItem("userId") || "admin";
      await fetchFirstOk(`reviews/${review._id}/publish`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isPublished: nextStatus,
          publishedBy,
        }),
      });

      setReviews((prev) =>
        prev.map((r) =>
          r._id === review._id
            ? {
              ...r,
              isPublished: nextStatus,
            }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating review status:", err);
      window.alert("Failed to update publish status");
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) {
      return;
    }

    try {
      await fetchFirstOk(`reviews/${reviewId}`, {
        method: "DELETE",
      });

      setReviews((prev) => prev.filter((r) => r._id !== reviewId));
    } catch (err) {
      console.error("Error deleting review:", err);
      window.alert("Failed to delete review");
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8F4F4]">
      <div className="max-sm:hidden flex">
        <Sidebar active={activePage} onNavigate={setActivePage} />
      </div>
      <div className="flex-1 py-6 px-8 max-h-screen min-h-screen overflow-scroll">
        <Header onSearch={handleSearch} />

        <div className="bg-white rounded-lg p-5 mt-4 shadow-sm">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h1 className="text-2xl font-semibold">Review Management</h1>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Status</label>
              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {error && <p className="text-red-600 mb-3">{error}</p>}

          {loading ? (
            <p className="text-gray-600">Loading reviews...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="p-3">Product</th>
                    <th className="p-3">User</th>
                    <th className="p-3">Rating</th>
                    <th className="p-3">Title</th>
                    <th className="p-3">Created</th>
                    <th className="p-3">Publish</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 && (
                    <tr>
                      <td className="p-4 text-gray-500" colSpan={7}>
                        No reviews found
                      </td>
                    </tr>
                  )}
                  {reviews.map((review) => (
                    <tr key={review._id} className="border-b border-gray-200">
                      <td className="p-3">
                        <div className="font-medium text-xs">{review.productName || "Unknown Product"}</div>
                        <div className="text-gray-400 text-[10px]">{review.productId}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{review.userName}</div>
                        <div className="text-gray-500 text-xs">{review.userEmail}</div>
                      </td>
                      <td className="p-3">{review.rating}/5</td>
                      <td className="p-3">
                        <div className="font-medium">{review.title}</div>
                        <div className="text-gray-500 text-xs line-clamp-2">{review.content}</div>
                      </td>
                      <td className="p-3">
                        {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : "-"}
                      </td>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={!!review.isPublished}
                          onChange={() => handlePublishToggle(review)}
                        />
                      </td>
                      <td className="p-3">
                        <button
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded"
                          onClick={() => handleDelete(review._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewManagement;
